import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import React, { useContext } from "react";
import { Avatar, Typography, Divider, IconButton, Tooltip } from "@mui/material";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useNavigate } from "react-router-dom";
import SideBarMenus from "./SideBarMenus";
import Header from "../header/Header";
import AuthContext from "../../store/auth-context";
import { resolveImageUrl } from "../../services/apiUtils";
import muradiMark from "../../assets/Images/muradiMark256.png";

const drawerWidth = 268;

const formatRoleName = (roleName?: string): string => {
  if (!roleName) return "User";
  return roleName
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const Brand = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 0.5,
      px: 2.5,
      height: 72,
      flexShrink: 0,
      boxSizing: "border-box",
    }}
  >
    {/* Transparent, high-resolution logo mark (no background box) with the brand name beside it */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        component="img"
        src={muradiMark}
        alt="Muradiz logo"
        sx={{ height: 46, width: "auto", flexShrink: 0, objectFit: "contain" }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ fontWeight: 800, fontSize: 24, lineHeight: 1.05, letterSpacing: "-0.02em", color: "#2E3092" }}
        >
          Muradiz
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: 12.5, lineHeight: 1.3, color: "text.secondary", mt: 0.25 }}>
          Seller Center
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default function SideBar() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const handleDrawerToggle = () => setMobileOpen((v) => !v);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const content = (onNavigate?: () => void) => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Brand />
      <Divider />
      <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
        <SideBarMenus onNavigate={onNavigate} />
      </Box>
      <Divider />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2 }}>
        <Avatar
          sx={{ width: 40, height: 40, bgcolor: "primary.light", color: "primary.main", fontWeight: 700 }}
          alt={user?.name || "User"}
          src={resolveImageUrl(user?.avatarUrl) || undefined}
        >
          {(user?.name || "U").charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {user?.name || "User"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {formatRoleName(user?.roleName)}
          </Typography>
        </Box>
        <Tooltip title="Log out">
          <IconButton size="small" onClick={handleLogout} sx={{ color: "text.secondary" }}>
            <LogoutRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <React.Fragment>
      <Header onHandleDrawerToggle={handleDrawerToggle} />
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="main navigation">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          {content(() => setMobileOpen(false))}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
          open
        >
          {content()}
        </Drawer>
      </Box>
    </React.Fragment>
  );
}
